import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../Layout/PublicLayout';
import { API_BASE } from '../../App';

function PublicWebsite() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState({
    totalPlayers: '10,000+',
    dailyTournaments: '500+',
    totalPayout: '₹10L+',
    rating: '4.8★'
  });

  useEffect(() => {
    fetchPublicData();
  }, []);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    fetchPublicData();

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsStandalone(true);
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Capture install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS instructions (simple alert for now or modal if preferred)
      alert("To install on iOS:\n1. Tap the Share button at the bottom\n2. Scroll down and tap 'Add to Home Screen'");
      return;
    }

    if (!deferredPrompt) {
      // If no prompt available, it might be already installed or not supported
      if (isStandalone) {
        alert("App is already installed!");
      } else {
        alert("To install: Tap the menu (⋮) -> 'Install App' or 'Add to Home Screen'");
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const fetchPublicData = async () => {
    try {
      // Fetch upcoming matches
      const matchesRes = await fetch(`${API_BASE}/matches?status=scheduled`);
      if (matchesRes.ok) {
        const matchesData = await matchesRes.json();
        setMatches(matchesData.slice(0, 6)); // Show only first 6
      }

      // Fetch completed matches for results
      const resultsRes = await fetch(`${API_BASE}/matches?status=completed`);
      if (resultsRes.ok) {
        const resultsData = await resultsRes.json();
        setResults(resultsData.slice(0, 4)); // Show only first 4
      }
    } catch (error) {
      console.error('Error fetching public data:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  const getPerPlayerCost = (match) => {
    let teamSize = 1;
    if (match.mode === 'duo') teamSize = 2;
    if (match.mode === 'squad') teamSize = 4;
    return match.entry_fee / teamSize;
  };

  return (
    <PublicLayout
      onLogin={() => navigate('/login')}
      onRegister={() => navigate('/register')}
    >
      {/* Hero Section */}
      <section id="home" style={{
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '40px 20px'
      }}>
        {/* Animated Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 240, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, rgba(0, 240, 255, 0.1) 0%, transparent 40%)
          `,
          animation: 'float 20s ease-in-out infinite'
        }} />

        <div style={{
          maxWidth: '1400px',
          width: '100%',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 'clamp(3rem, 8vw, 6rem)',
            marginBottom: '30px',
            background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'glow 3s ease-in-out infinite alternate'
          }}>
            BATTLE. WIN. DOMINATE.
          </h1>

          <p style={{
            fontSize: 'clamp(1.2rem, 3vw, 2rem)',
            color: 'var(--text-secondary)',
            marginBottom: '20px',
            maxWidth: '900px',
            margin: '0 auto 20px',
            lineHeight: '1.6'
          }}>
            India's Most Trusted <span style={{ color: 'var(--primary-color)', fontWeight: 'bold' }}>BGMI Tournament Platform</span>
          </p>

          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.3rem)',
            color: 'var(--text-secondary)',
            marginBottom: '50px',
            maxWidth: '800px',
            margin: '0 auto 50px'
          }}>
            Compete in daily tournaments, win real money, and get paid instantly to your wallet!
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '60px'
          }}>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/register")}
              style={{
                padding: '18px 50px',
                fontSize: '1.3rem',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            >
              Start Playing Now
            </button>
            <button
              className="btn btn-outline"
              onClick={() => scrollToSection('matches')}
              style={{
                padding: '18px 50px',
                fontSize: '1.3rem'
              }}
            >
              View Tournaments
            </button>
          </div>

          {/* Stats Bar */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '30px',
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '40px',
            background: 'rgba(26, 31, 58, 0.6)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            border: '1px solid rgba(0, 240, 255, 0.2)'
          }}>
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  fontFamily: 'Orbitron, sans-serif',
                  color: 'var(--primary-color)',
                  marginBottom: '8px'
                }}>
                  {value}
                </div>
                <div style={{
                  fontSize: '0.95rem',
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{
        padding: '100px 20px',
        background: 'rgba(0, 240, 255, 0.02)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            textAlign: 'center',
            marginBottom: '20px',
            color: 'var(--primary-color)'
          }}>
            Why ZeroPing?
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            maxWidth: '700px',
            margin: '0 auto 60px'
          }}>
            The ultimate platform designed for serious BGMI players
          </p>

          <div className="grid grid-3" style={{ gap: '30px' }}>
            {[
              {
                icon: '',
                title: 'Instant Payouts',
                desc: 'Win and get paid instantly to your wallet. Withdraw anytime with zero delays. Your money, your control.',
                gradient: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 240, 255, 0.05))'
              },
              {
                icon: '',
                title: 'Daily Tournaments',
                desc: 'Join unlimited tournaments across Solo, Duo, and Squad modes. Multiple matches every hour, every day.',
                gradient: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 215, 0, 0.05))'
              },
              {
                icon: '',
                title: '100% Secure',
                desc: 'Bank-level encryption, RBI compliant payments. Your data and money are completely safe with us.',
                gradient: 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 255, 136, 0.05))'
              },
              {
                icon: '',
                title: 'Low Entry Fees',
                desc: 'Start playing from just ₹10! Big prizes, small investments. Everyone can compete and win.',
                gradient: 'linear-gradient(135deg, rgba(255, 0, 255, 0.1), rgba(255, 0, 255, 0.05))'
              },
              {
                icon: '',
                title: 'Team Support',
                desc: 'Create teams, manage members, and dominate squad tournaments together. Team prizes auto-split!',
                gradient: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 240, 255, 0.05))'
              },
              {
                icon: '',
                title: 'Live Results',
                desc: 'Real-time match results, transparent prize distribution. Track every rupee you earn or spend.',
                gradient: 'linear-gradient(135deg, rgba(255, 0, 255, 0.1), rgba(255, 0, 255, 0.05))'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="card"
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  background: feature.gradient,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 240, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>{feature.icon}</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  marginBottom: '15px',
                  color: 'var(--primary-color)',
                  fontFamily: 'Orbitron, sans-serif'
                }}>
                  {feature.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', fontSize: '1.05rem' }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Install App Section */}
      <section id="install" style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(10, 14, 39, 0.95))',
        borderTop: '1px solid var(--border-color)',
        borderBottom: '1px solid var(--border-color)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Glow */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(0, 240, 255, 0.05) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: '#fff',
            marginBottom: '20px',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
          }}>
            Play Anywhere, Anytime
          </h2>

          <p style={{
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            marginBottom: '40px',
            maxWidth: '600px'
          }}>
            Download the ZeroPing app for the best experience. Get instant notifications, faster load times, and smoother gameplay.
          </p>

          <div style={{
            display: 'flex',
            gap: '30px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '20px 40px',
              borderRadius: '15px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{ fontSize: '2rem' }}></span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Lightweight</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>&lt; 5 MB</div>
              </div>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '20px 40px',
              borderRadius: '15px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{ fontSize: '2rem' }}></span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}> Updates</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Instant</div>
              </div>
            </div>
          </div>

          {!isStandalone && (
            <button
              onClick={handleInstallClick}
              className="btn"
              style={{
                background: 'linear-gradient(135deg, #00f0ff, #0066ff)',
                color: '#000',
                padding: '20px 60px',
                fontSize: '1.4rem',
                fontWeight: 'bold',
                borderRadius: '50px',
                border: 'none',
                boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 0 50px rgba(0, 240, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.3)';
              }}
            >
              <span>{isIOS ? 'Install on iOS' : 'Install App'}</span>
            </button>
          )}

          {isStandalone && (
            <div style={{
              background: 'rgba(0, 255, 136, 0.1)',
              padding: '15px 30px',
              borderRadius: '50px',
              color: 'var(--success-color)',
              border: '1px solid var(--success-color)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span></span> App is Installed
            </div>
          )}
        </div>
      </section>

      {/* Matches Section */}
      <section id="matches" style={{ padding: '100px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            textAlign: 'center',
            marginBottom: '20px',
            color: 'var(--primary-color)'
          }}>
            Upcoming Tournaments
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            marginBottom: '60px'
          }}>
            Join live tournaments happening right now!
          </p>

          {matches.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ fontSize: '3rem', marginBottom: '20px' }}></p>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>Loading tournaments...</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Please wait while we fetch upcoming matches
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-3" style={{ gap: '30px', marginBottom: '40px' }}>
                {matches.map((match, index) => (
                  <div
                    key={match.id}
                    className="card"
                    style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.borderColor = 'var(--primary-color)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    <h3 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>{match.title}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Map:</span>
                        <span style={{ fontWeight: 'bold' }}>{match.map}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Mode:</span>
                        <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{match.mode}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Prize Pool:</span>
                        <span style={{ color: '#ffb700', fontWeight: 'bold' }}>
                          ₹{match.prize_pool_bigint ? (match.prize_pool_bigint / 100).toLocaleString() : '0'}
                        </span>
                      </div>

                      {/* Per Kill Display - ADDED */}
                      {match.per_kill > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Per Kill:</span>
                          <span style={{ color: '#ff4757', fontWeight: 'bold' }}>
                            ₹{match.per_kill}
                          </span>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Entry:</span>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ color: 'var(--success-color)', fontWeight: 'bold' }}>
                            ₹{match.entry_fee}
                          </span>
                          {/* Per Player Cost - ADDED */}
                          {match.mode !== 'solo' && match.entry_fee > 0 && (
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                              (₹{getPerPlayerCost(match)}/player)
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Starts:</span>
                        <span style={{ fontSize: '0.9rem' }}>
                          {new Date(match.start_ts).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/login")}
                      style={{ width: '100%' }}
                    >
                      Join Tournament
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => navigate("/login")}
                  style={{ padding: '15px 40px', fontSize: '1.1rem' }}
                >
                  View All Tournaments →
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Results Section */}
      <section id="results" style={{
        padding: '100px 20px',
        background: 'rgba(255, 0, 255, 0.02)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            textAlign: 'center',
            marginBottom: '20px',
            color: 'var(--secondary-color)'
          }}>
            🏆 Recent Winners
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            marginBottom: '60px'
          }}>
            Transparent results, instant payouts
          </p>

          {results.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
              <p style={{ fontSize: '3rem', marginBottom: '20px' }}></p>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No results yet</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Completed tournament results will appear here
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              {results.map((match, index) => (
                <div
                  key={match.id}
                  className="card"
                  style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both` }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    gap: '15px'
                  }}>
                    <div>
                      <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>{match.title}</h3>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>{match.map}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{match.mode.toUpperCase()}</span>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {new Date(match.start_ts).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <span className="badge badge-success">Completed</span>
                  </div>

                  <div style={{
                    background: 'var(--accent-bg)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <p style={{
                      color: 'var(--text-secondary)',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}>
                      Results declared • Prize money distributed
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ZeroPing TV / YouTube Section */}
      <section id="zeroping-tv" style={{
        padding: '80px 20px',
        background: 'linear-gradient(to right, #000000, #1a0b0b)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background accent */}
        <div style={{
          position: 'absolute',
          top: '0',
          right: '0',
          width: '50%',
          height: '100%',
          background: 'radial-gradient(circle at center, rgba(255, 0, 0, 0.1) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '50px' }}>

          {/* Text Content */}
          <div style={{ flex: '1 1 400px' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(255, 0, 0, 0.2)',
              color: '#ff4d4d',
              padding: '5px 15px',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              marginBottom: '15px'
            }}>
              🔴 LIVE STREAMING SOON
            </div>
            <h2 style={{
              fontFamily: 'Orbitron, sans-serif',
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              marginBottom: '20px',
              color: '#fff',
              lineHeight: '1.1'
            }}>
              ZEROPING <span style={{ color: '#ff0000' }}>TV</span>
            </h2>
            <p style={{
              fontSize: '1.2rem',
              color: '#ccc',
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              Watch top-tier esports highlights, insane clutches, and tournament live streams.
              Don't miss the action – subscribe to our official channel now!
            </p>
            <a href="https://youtube.com/@TheZeroPing" target="_blank" rel="noopener noreferrer" className="btn" style={{
              background: '#ff0000',
              color: '#fff',
              padding: '10px 40px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: '50px',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '15px',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.4)',
              border: 'none',
              cursor: 'pointer'
            }}>
              <img src="/YouTube_social_white_square_(2024).svg.png" alt="YouTube" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
              Subscribe @TheZeroPing
            </a>
          </div>

          {/* Video Placeholder / Visual */}
          <div style={{ flex: '1 1 500px', position: 'relative' }}>
            <div style={{
              background: '#000',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid #333',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
              aspectRatio: '16/9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {/* Mock Thumbnail showing game action or logo */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'linear-gradient(45deg, #1a1f3a, #252b4a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'rgba(255,255,255,0.2)',
                textDecoration: 'none'
              }}>
                <span style={{ fontSize: '4rem' }}></span>
                <span style={{ marginTop: '10px', fontWeight: 'bold' }}>HIGHLIGHTS REEL</span>
              </div>

              {/* Play Button Overlay */}
              <a href="https://youtube.com/@TheZeroPing" target="_blank" rel="noopener noreferrer" style={{
                width: '80px',
                height: '80px',
                background: 'rgba(255,0,0,0.9)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '2rem',
                cursor: 'pointer',
                zIndex: 2,
                boxShadow: '0 0 0 10px rgba(255,0,0,0.2)',
                transition: 'transform 0.2s',
                textDecoration: 'none'
              }}>
                ▶
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Compact Community Bar */}
      <section id="community" style={{
        padding: '60px 20px',
        borderTop: '1px solid var(--border-color)',
        borderBottom: '1px solid var(--border-color)',
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 240, 255, 0.05))',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: '2rem',
            marginBottom: '15px',
            color: '#fff'
          }}>
            Join the <span style={{ color: '#25D366' }}>Community</span>
          </h2>

          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            marginBottom: '30px',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.6'
          }}>
            Join the official ZeroPing community! Connect with other players, find teammates for Duo/Squad matches, get instant tournament updates, and participate in exclusive community events.
          </p>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <a href="https://instagram.com/zeroping.app" target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              color: '#fff', textDecoration: 'none',
              background: 'rgba(255,255,255,0.05)', padding: '12px 30px', borderRadius: '50px',
              transition: 'all 0.2s',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'linear-gradient(45deg, #405DE6, #E1306C)';
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <img src="/Instagram_logo_2022.svg.webp" alt="Instagram" style={{ width: '24px', height: '24px', objectFit: 'contain' }} /> Instagram
            </a>

            <a href="#" style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              color: '#fff', textDecoration: 'none',
              background: 'rgba(255,255,255,0.05)', padding: '12px 30px', borderRadius: '50px',
              transition: 'all 0.2s',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#0088cc';
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <img src="/Telegram_2019_Logo.svg.webp" alt="Telegram" style={{ width: '24px', height: '24px', objectFit: 'contain' }} /> Telegram
            </a>

            <a href="https://chat.whatsapp.com/F04COPemmeB70tPvvp6YOV" target="_blank" rel="noopener noreferrer" style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              color: '#fff', textDecoration: 'none',
              background: 'rgba(255,255,255,0.05)', padding: '12px 30px', borderRadius: '50px',
              transition: 'all 0.2s',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#25D366';
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <img src="/WhatsApp_Logo_green.svg.webp" alt="WhatsApp" style={{ width: '24px', height: '24px', objectFit: 'contain' }} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" style={{ padding: '100px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            textAlign: 'center',
            marginBottom: '20px',
            color: 'var(--primary-color)'
          }}>
            ℹ️ About ZeroPing
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            marginBottom: '60px',
            maxWidth: '800px',
            margin: '0 auto 60px'
          }}>
            India's most trusted BGMI tournament platform
          </p>

          <div style={{ marginBottom: '60px' }}>
            <div className="card" style={{ padding: '50px', textAlign: 'center' }}>
              <h3 style={{
                fontSize: '2rem',
                marginBottom: '30px',
                color: 'var(--primary-color)',
                fontFamily: 'Orbitron, sans-serif'
              }}>
                Our Mission
              </h3>
              <p style={{
                fontSize: '1.2rem',
                lineHeight: '1.8',
                color: 'var(--text-secondary)',
                maxWidth: '900px',
                margin: '0 auto'
              }}>
                We're on a mission to revolutionize esports in India by providing a <strong style={{ color: 'var(--primary-color)' }}>fair, transparent, and secure platform</strong> for BGMI players to showcase their skills and earn real money. Every gamer deserves a chance to compete at the highest level, regardless of their background.
              </p>
            </div>
          </div>

          {/* Final CTA */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(255, 0, 255, 0.1))',
            padding: '60px 40px',
            borderRadius: '20px',
            textAlign: 'center',
            border: '2px solid rgba(0, 240, 255, 0.3)'
          }}>
            <h3 style={{
              fontSize: '2.5rem',
              marginBottom: '20px',
              fontFamily: 'Orbitron, sans-serif'
            }}>
              Ready to Join the Elite?
            </h3>
            <p style={{
              fontSize: '1.2rem',
              color: 'var(--text-secondary)',
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px'
            }}>
              Join thousands of players winning real money every day. Your next big win is just one click away!
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/register")}
                style={{ padding: '18px 50px', fontSize: '1.2rem' }}
              >
                🚀 Create Free Account
              </button>
              <button
                className="btn btn-outline"
                onClick={() => navigate("/login")}
                style={{ padding: '18px 50px', fontSize: '1.2rem' }}
              >
                🔐 Login
              </button>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export default PublicWebsite;
